import React, { PropsWithChildren } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import SummaryNavigation, { ISummaryNavigationProps } from '@components/UI/SummaryNavigation'
import { ICampaignContextProps } from '@context/Campaign'
import { ISubmissionContextProps } from '@context/Submission'
import { ISubmissionListFilterContextProps } from '@context/SubmissionListFilter'
import { EventTrackingNames } from '@enums'
import {
  withCampaignNoRenderBlock,
  withSubmissionListFilter,
  withSubmissionNoRenderBlock,
} from '@hocs'
import hooks from '@hooks'
import { IApplicationState } from '@store'
import { selectLocationsearch } from '@store/router'
import {
  fetchSubmissions,
  selectFromSubmissionId,
  selectIsFetching,
  selectLoadMoreSubmissions,
  selectNextSubmissionId,
  selectPrevSubmissionId,
} from '@store/submissions'
import { buildSubmissionUrlById } from '@utils/urls'

export interface IInternalProps
  extends ISummaryNavigationProps,
    ISubmissionListFilterContextProps,
    ICampaignContextProps,
    ISubmissionContextProps {}

export const SubmissionSummaryNavigationByFilter: React.FC<PropsWithChildren<IInternalProps>> = ({
  submission,
  campaign,
  filter,
  sortBy,
  children,
}) => {
  const dispatch = useDispatch()
  const campaignId = campaign.id

  const searchParams = useSelector((state: IApplicationState) => selectLocationsearch(state))
  const fromSubmissionId = selectFromSubmissionId(searchParams, submission.id)

  const nextSubmissionId = useSelector((state: IApplicationState) =>
    selectNextSubmissionId(state, campaignId, filter, fromSubmissionId),
  )
  const prevSubmissionId = useSelector((state: IApplicationState) =>
    selectPrevSubmissionId(state, campaignId, filter, fromSubmissionId),
  )
  const isFetching = useSelector((state: IApplicationState) => selectIsFetching(state))
  const loadingMore = isFetching && Boolean(nextSubmissionId)
  const loadMoreSubmissions = useSelector((state: IApplicationState) =>
    selectLoadMoreSubmissions(state, campaignId, filter, fromSubmissionId),
  )

  const prevLink = buildSubmissionUrlById(campaignId, prevSubmissionId) as string | undefined
  const nextLink = buildSubmissionUrlById(campaignId, nextSubmissionId) as string | undefined

  hooks.useActionDispatcherEffect(
    Boolean(loadMoreSubmissions),
    (...args: Parameters<typeof fetchSubmissions>) => dispatch(fetchSubmissions(...args)),
    [campaignId, { filter, sortBy }, true],
    [loadMoreSubmissions],
  )

  return (
    <SummaryNavigation
      prevLink={prevLink}
      nextLink={nextLink}
      currentId={submission.id}
      nextTrackingProps={{
        campaignId,
        eventName: EventTrackingNames.ViewSubmission,
        submissionId: nextSubmissionId,
      }}
      prevTrackingProps={{
        campaignId,
        eventName: EventTrackingNames.ViewSubmission,
        submissionId: prevSubmissionId,
      }}
      loadingMore={loadingMore}
    >
      {children}
    </SummaryNavigation>
  )
}

export default withCampaignNoRenderBlock(
  withSubmissionListFilter(withSubmissionNoRenderBlock(SubmissionSummaryNavigationByFilter)),
)
